import { useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useBreakpointValue } from '@chakra-ui/react';
import { ChatMessage } from '@/types/chatContext';
import type { PendingAction } from '@/types/chat';
import { ROUTES } from '@/types';
import { ChatContext, type OpenChatOptions } from '@/hooks/useChatContext';
import AIChefChat from '@/components/AIChefChat';

const SESSION_KEY = 'bp_chat_session';

interface PersistedChatState {
  conversationId: string | null;
  messages: ChatMessage[];
  currentRecipeId: string | null;
  currentCollectionId: string | null;
  pendingAction: PendingAction | null;
}

function loadFromStorage(): PersistedChatState {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw) as PersistedChatState;
  } catch {
    // ignore — private browsing or corrupted data
  }
  return { conversationId: null, messages: [], currentRecipeId: null, currentCollectionId: null, pendingAction: null };
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const initial = loadFromStorage();
  const [conversationId, setConversationId] = useState<string | null>(initial.conversationId);
  const [messages, setMessages] = useState<ChatMessage[]>(initial.messages);
  const [currentRecipeId, setCurrentRecipeId] = useState<string | null>(initial.currentRecipeId);
  const [currentCollectionId, setCurrentCollectionId] = useState<string | null>(initial.currentCollectionId ?? null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    initial.pendingAction ?? null
  );
  const [desktopChatOpen, setDesktopChatOpen] = useState(false);
  const [helpMode, setHelpMode] = useState(false);

  const [, navigate] = useLocation();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  useEffect(() => {
    try {
      localStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ conversationId, messages, currentRecipeId, currentCollectionId, pendingAction })
      );
    } catch {
      // ignore storage errors (e.g. quota exceeded)
    }
  }, [conversationId, messages, currentRecipeId, currentCollectionId, pendingAction]);

  const resetChat = useCallback(() => {
    setConversationId(null);
    setMessages([]);
    setPendingAction(null);
    // currentRecipeId and currentCollectionId are intentionally not reset here
  }, []);

  // Single entry point for opening the chat. Scope is set only here (never
  // ambiently from the viewed page), so currentRecipeId tracks the conversation
  // and a new recipe/collection is detected and reset correctly.
  const openChat = useCallback((opts: OpenChatOptions = {}) => {
    const nextRecipeId = 'recipeId' in opts ? (opts.recipeId ?? null) : currentRecipeId;
    const nextCollectionId = 'collectionId' in opts ? (opts.collectionId ?? null) : currentCollectionId;

    // Fresh conversation for a new recipe/collection, or per queued substitution.
    const scopeChanged = nextRecipeId !== currentRecipeId || nextCollectionId !== currentCollectionId;
    if (scopeChanged || opts.pendingAction) {
      resetChat();
    }

    setCurrentRecipeId(nextRecipeId);
    setCurrentCollectionId(nextCollectionId);
    setHelpMode(!!opts.helpMode);
    if (opts.pendingAction) {
      setPendingAction(opts.pendingAction);
    }
    if (isMobile) {
      navigate(ROUTES.AI_CHEF());
    } else {
      setDesktopChatOpen(true);
    }
  }, [isMobile, navigate, currentRecipeId, currentCollectionId, resetChat]);

  const closeChat = useCallback(() => {
    setDesktopChatOpen(false);
    setHelpMode(false);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        conversationId,
        messages,
        currentRecipeId,
        currentCollectionId,
        pendingAction,
        desktopChatOpen,
        helpMode,
        setConversationId,
        setMessages,
        setCurrentRecipeId,
        setCurrentCollectionId,
        setPendingAction,
        setDesktopChatOpen,
        setHelpMode,
        openChat,
        closeChat,
        resetChat,
      }}
    >
      {children}
      {desktopChatOpen && (
        <AIChefChat onClose={closeChat} variant="widget" />
      )}
    </ChatContext.Provider>
  );
}
